/**
 *  @swagger
 *  definitions:
 *    RegattaDayParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        NameDate:
 *          description: Name Date of the RegattaDay
 *          type: string
 *          format: date
 *
 *  @swagger
 *  definitions:
 *    RegattaDayCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDayParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - NameDate
 *
 *  @swagger
 *  definitions:
 *    RegattaDayQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaDay
 *              type: string
 *        - $ref: '#/definitions/RegattaDayParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaDayUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDayQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaDay:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDayCreateParams'
 *        - $ref: '#/definitions/RegattaDayUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaDay:
 *        allOf:
 *          - $ref: '#/definitions/RegattaDay'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaDay
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  NameDate: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
  RegattaProtestEnds?: Array<$Model<'RegattaProtestEnd'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  NameDate: {
    allowNull: false,
    type: Sequelize.DATEONLY,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaDay'> => {
  const RegattaDay = stack.define(
    'RegattaDay',
    fields(Sequelize),
    {
      tableName: 'RegattaDays',
    },
  );

  RegattaDay.associate = (models: $ModelClasses) => {
    models.RegattaDay.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaDay.hasMany(
      models.RegattaProtestEnd,
      {
        as: 'RegattaProtestEnds',
        foreignKey: 'RegattaDayId',
      },
    );
  };

  RegattaDay.addScope(
    'defaultScope',
    {
      order: [['NameDate', 'ASC']],
    },
  );

  return RegattaDay;
});
